import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Mail, Phone, LogOut, Pencil, Lock, ShieldCheck } from 'lucide-react';
import BottomNav from '../../components/BottomNav';
import { toast } from 'sonner';
import EditNameInline from '../../components/profile/EditNameInline';
import PhoneChangeDialog from '../../components/profile/PhoneChangeDialog';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isGuest, logout, updateUser } = useAuth();
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

  // Auth gate lives in <CustomerProtectedRoute /> (see App.js) — by the
  // time we render, `user` is guaranteed non-null.

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    setTimeout(() => {
      navigate('/');
    }, 0);
  };

  if (!user) {
    return null;
  }

  return (
    <div className="mobile-container min-h-screen pb-20" data-testid="profile-page">
      <div className="bg-primary text-primary-foreground p-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home')}
            data-testid="back-button"
            className="text-primary-foreground hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-black font-heading uppercase">Profile</h1>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Avatar + Editable Fields */}
        <Card className="p-6 glass-card">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-accent rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-accent-foreground" />
            </div>
          </div>

          <div className="space-y-5">
            {!isGuest && user && (
              <>
                {/* Name (editable, no OTP) */}
                <div>
                  <p className="text-xs uppercase font-bold text-muted-foreground mb-1.5">Name</p>
                  <EditNameInline
                    currentName={user.name}
                    onUpdated={updateUser}
                  />
                </div>

                {/* Email (locked) */}
                <div>
                  <p className="text-xs uppercase font-bold text-muted-foreground mb-1.5">Email</p>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Mail className="w-5 h-5 text-muted-foreground shrink-0" />
                      <p className="font-mono text-sm truncate" data-testid="profile-email-value">
                        {user.email}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className="border-emerald-500 text-emerald-600 bg-emerald-50 shrink-0"
                      data-testid="email-verified-badge"
                    >
                      <ShieldCheck className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Email cannot be changed
                  </p>
                </div>

                {/* Phone (editable with OTP) */}
                <div>
                  <p className="text-xs uppercase font-bold text-muted-foreground mb-1.5">Phone</p>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Phone className="w-5 h-5 text-muted-foreground shrink-0" />
                      <p className="font-mono text-sm truncate" data-testid="profile-phone-value">
                        {user.phone}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setPhoneDialogOpen(true)}
                      data-testid="edit-phone-button"
                      aria-label="Edit phone"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
            {isGuest && user && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground mb-1">Phone (Guest)</p>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground" />
                  <p className="font-mono text-sm">{user?.phone}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Account Type */}
        <Card className="p-4 glass-card bg-muted/30">
          <p className="text-sm text-center text-muted-foreground">
            {isGuest ? 'Guest Account' : 'Registered Account'}
          </p>
        </Card>

        {/* Logout */}
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="destructive"
          className="w-full h-14 rounded-full"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Logout
        </Button>
      </div>

      {/* Phone Change Dialog */}
      {!isGuest && user && (
        <PhoneChangeDialog
          open={phoneDialogOpen}
          onOpenChange={setPhoneDialogOpen}
          currentPhone={user.phone}
          registeredEmail={user.email}
          onUpdated={updateUser}
        />
      )}
      <BottomNav />
    </div>
  );
}
